// components/BrokerMonthlyBar.jsx
import { useEffect, useState, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js";
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

export default function BrokerMonthlyBar({ brokerAccountId, heightClass = "h-40" }) {
  const [dataPoints, setDataPoints] = useState(Array(12).fill(0));

  // build last 12 month labels like ["Sep", "Oct", ...]
  const labels = useMemo(() => {
    const arr = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 11; i >= 0; i--) {
      const tmp = new Date(d);
      tmp.setMonth(d.getMonth() - i);
      arr.push(tmp.toLocaleString("en-US", { month: "short" }));
    }
    return arr;
  }, []);

  useEffect(() => {
    (async () => {
      if (!brokerAccountId) {
        setDataPoints(Array(12).fill(0));
        return;
      }

      // Pull this broker account's intakes in the last 12 months. Broker owners
      // and assistants share the same account-level dashboard.
      const start = new Date();
      start.setHours(0,0,0,0);
      start.setMonth(start.getMonth() - 11);
      start.setDate(1);

      const { data, error } = await supabase
        .from("broker_intake")
        .select("created_at")
        .eq("broker_account_id", brokerAccountId)
        .gte("created_at", start.toISOString());

      if (error) {
        console.error("MonthlyBar fetch error:", error);
        return;
      }

      // Zero-fill 12 buckets, then increment per month.
      const buckets = Array(12).fill(0);
      const base = new Date(start); // first of month, 11 months ago
      data?.forEach(({ created_at }) => {
        const dt = new Date(created_at);
        const diff =
          (dt.getFullYear() - base.getFullYear()) * 12 +
          (dt.getMonth() - base.getMonth());
        if (diff >= 0 && diff < 12) buckets[diff]++;
      });
      setDataPoints(buckets);
    })();
  }, [brokerAccountId]);

  const chartData = {
    labels,
    datasets: [
      {
        data: dataPoints,
        borderWidth: 0,
        // ChartJS will use default colors; if you want a single brand color:
        backgroundColor: "rgba(211,34,42,0.25)",
        hoverBackgroundColor: "rgba(211,34,42,0.5)",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { grid: { display: false } },
    },
  };

  return (
    <div className={heightClass}>
      <Bar data={chartData} options={options} />
    </div>
  );
}
